import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import * as LuIcons from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';
import { format } from 'date-fns';

const { LuUsers, LuCheck, LuCopy, LuTrendingUp, LuLock, LuPhone, LuAlertTriangle, LuClock, LuCheckCircle, LuXCircle, LuLogOut, LuTicket, LuCar, LuHeartHandshake, LuExternalLink, LuNetwork, LuUserPlus, LuDollarSign, LuChevronLeft } = LuIcons;

const AffiliatePage = () => {
  const [view, setView] = useState('login'); // login, terms, register, dashboard
  const [activeTab, setActiveTab] = useState('dashboard'); // dashboard, cars, network  (inside dashboard view)
  const [loading, setLoading] = useState(false);
  const [marketerData, setMarketerData] = useState(null);
  const [settings, setSettings] = useState({ 
    affiliate_reg_open: 'true', 
    affiliate_terms: '',
    affiliate_reg_title: 'إنشاء حساب مسوق',
    affiliate_reg_subtitle: 'أدخل بياناتك لإنشاء حسابك والبدء في التسويق'
  });

  // Login State
  const [loginPhone, setLoginPhone] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [loginError, setLoginError] = useState('');

  // Register State
  const [regData, setRegData] = useState({ name: '', phone: '', password: '', referred_by: '' });
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Dashboard State (Direct)
  const [leads, setLeads] = useState([]);
  const [marketCars, setMarketCars] = useState([]);
  
  // Dashboard State (Network/MLM)
  const [networkTeam, setNetworkTeam] = useState([]);
  const [networkLeads, setNetworkLeads] = useState([]);

  // UI State
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(null);

  useEffect(() => {
    fetchSettings();
    const savedSession = sessionStorage.getItem('marketer_session');
    if (savedSession) {
      const data = JSON.parse(savedSession);
      setMarketerData(data);
      setView('dashboard');
      fetchDashboardData(data.code);
      fetchNetworkData(data.code);
      fetchMarketCars();
    }
  }, []);

  const fetchSettings = async () => {
    const { data } = await supabase.from('site_settings')
      .select('setting_key, setting_value')
      .in('setting_key', ['affiliate_reg_open', 'affiliate_terms', 'affiliate_reg_title', 'affiliate_reg_subtitle']);
    if (data) {
      const s = { 
        affiliate_reg_open: 'true', 
        affiliate_terms: '',
        affiliate_reg_title: 'إنشاء حساب مسوق',
        affiliate_reg_subtitle: 'أدخل بياناتك لإنشاء حسابك والبدء في التسويق'
      };
      data.forEach(d => s[d.setting_key] = d.setting_value);
      setSettings(s);
    }
  };

  const fetchDashboardData = async (code) => {
    const { data: leadsData } = await supabase.from('leads')
      .select('*, cars_for_sale(brand, model, commission)')
      .eq('marketer_code', code)
      .order('created_at', { ascending: false });
    if (leadsData) setLeads(leadsData);
  };

  const fetchNetworkData = async (myCode) => {
    // 1. Get all marketers invited by this user
    const { data: team } = await supabase.from('marketers')
      .select('*')
      .eq('referred_by', myCode)
      .order('created_at', { ascending: false });
      
    setNetworkTeam(team || []);

    // 2. If there is a team, get their leads
    if (team && team.length > 0) {
      const teamCodes = team.map(t => t.code);
      const { data: teamLeads } = await supabase.from('leads')
        .select('*, cars_for_sale(brand, model, parent_commission)')
        .in('marketer_code', teamCodes)
        .order('created_at', { ascending: false });
      setNetworkLeads(teamLeads || []);
    }
  };

  const fetchMarketCars = async () => {
    const { data: carsData } = await supabase.from('cars_for_sale')
      .select('id, brand, model, year, total_price, price, customs_price, shipping_price, image_url, commission, parent_commission')
      .eq('status', 'available')
      .order('created_at', { ascending: false });
    if (carsData) setMarketCars(carsData);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLoginError('');
    try {
      const { data, error } = await supabase.from('marketers').select('*').eq('phone', loginPhone).single();
      if (error || !data) throw new Error('رقم الهاتف غير مسجل.');
      if (data.password !== loginPass) throw new Error('كلمة المرور غير صحيحة.');
      setMarketerData(data);
      sessionStorage.setItem('marketer_session', JSON.stringify(data));
      setView('dashboard');
      fetchDashboardData(data.code);
      fetchNetworkData(data.code);
      fetchMarketCars();
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // 1. Check if phone is already registered
      const { data: existing } = await supabase.from('marketers').select('id').eq('phone', regData.phone).single();
      if (existing) {
        alert('رقم الهاتف مسجل مسبقاً. يرجى تسجيل الدخول.');
        setView('login');
        return;
      }

      // 2. Validate referral code if provided
      let finalReferredBy = null;
      if (regData.referred_by.trim()) {
        const refCode = regData.referred_by.trim();
        const { data: parentMarketer } = await supabase.from('marketers').select('code').eq('code', refCode).single();
        if (!parentMarketer) {
          alert('كود المسوق الداعي الذي أدخلته غير صحيح. يرجى التأكد منه أو تركه فارغاً.');
          setLoading(false);
          return;
        }
        finalReferredBy = parentMarketer.code;
      }

      // 3. Create new account
      const code = `M-${Math.floor(1000 + Math.random() * 9000)}`;
      const { data, error } = await supabase.from('marketers').insert([{
        name: regData.name,
        phone: regData.phone,
        password: regData.password,
        code,
        referred_by: finalReferredBy,
        status: 'pending'
      }]).select().single();
      
      if (error) throw error;
      
      setMarketerData(data);
      sessionStorage.setItem('marketer_session', JSON.stringify(data));
      setView('dashboard');
    } catch (err) {
      alert(`خطأ: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('marketer_session');
    setMarketerData(null);
    setView('login');
  };

  const copyCode = () => {
    navigator.clipboard.writeText(marketerData.code);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const copyCarLink = (carId) => {
    const link = `${window.location.origin}/#/car/${carId}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(carId);
    setTimeout(() => setCopiedLink(null), 2000);
  };

  const renderStatusBadge = (status) => {
    if (status === 'approved') return <span className="bg-emerald-100 text-emerald-700 px-3 py-1 rounded-lg text-sm font-bold flex items-center gap-1"><SafeIcon icon={LuCheckCircle} className="w-4 h-4"/>نشط ومقبول</span>;
    if (status === 'rejected') return <span className="bg-red-100 text-red-700 px-3 py-1 rounded-lg text-sm font-bold flex items-center gap-1"><SafeIcon icon={LuXCircle} className="w-4 h-4"/>مرفوض</span>;
    return <span className="bg-amber-100 text-amber-700 px-3 py-1 rounded-lg text-sm font-bold flex items-center gap-1"><SafeIcon icon={LuClock} className="w-4 h-4"/>قيد المراجعة</span>;
  };

  // Calculations for earnings
  const myClosedLeads = leads.filter(l => l.status === 'closed');
  const myEarnings = myClosedLeads.reduce((sum, l) => sum + (Number(l.cars_for_sale?.commission) || 0), 0);
  
  const teamClosedLeads = networkLeads.filter(l => l.status === 'closed');
  const teamEarnings = teamClosedLeads.reduce((sum, l) => sum + (Number(l.cars_for_sale?.parent_commission) || 0), 0);

  const totalEarnings = myEarnings + teamEarnings;

  return (
    <div className="flex-1 w-full max-w-6xl mx-auto px-4 py-8 flex flex-col items-center">
      {/* ---------------- LOGIN VIEW ---------------- */}
      {view === 'login' && (
        <div className="w-full max-w-md mt-10">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={LuUsers} className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-3xl font-black text-slate-900 mb-2">بوابة المسوقين</h1>
            <p className="text-slate-500">سجل دخولك لمتابعة أرباحك وطلباتك</p>
          </div>

          <form onSubmit={handleLogin} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
            {loginError && <div className="bg-red-50 text-red-600 p-3 rounded-xl text-sm mb-4 border border-red-100">{loginError}</div>}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">رقم الهاتف</label>
                <div className="relative">
                  <SafeIcon icon={LuPhone} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <input required type="text" value={loginPhone} onChange={e => setLoginPhone(e.target.value)} dir="ltr" className="w-full p-3 pr-10 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-right" placeholder="0555000000" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1">كلمة المرور</label>
                <div className="relative">
                  <SafeIcon icon={LuLock} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                  <input required type="password" value={loginPass} onChange={e => setLoginPass(e.target.value)} dir="ltr" className="w-full p-3 pr-10 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-right" placeholder="••••••••" />
                </div>
              </div>
              <button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl transition-colors">
                {loading ? 'جاري الدخول...' : 'تسجيل الدخول'}
              </button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <p className="text-slate-600 text-sm">ليس لديك حساب مسوق؟</p>
            <button onClick={() => setView('terms')} className="text-blue-600 font-bold mt-2 hover:underline">انضم لبرنامج التسويق بالعمولة</button>
          </div>
        </div>
      )}

      {/* ---------------- TERMS VIEW ---------------- */}
      {view === 'terms' && (
        <div className="w-full max-w-3xl mt-10 bg-white p-6 sm:p-8 rounded-2xl shadow-sm border border-slate-200">
          <h2 className="text-2xl font-black text-slate-900 mb-6">شروط وأحكام التسويق بالعمولة</h2>
          
          {settings.affiliate_reg_open === 'false' ? (
            <div className="bg-amber-50 border border-amber-200 p-6 rounded-xl text-center">
              <SafeIcon icon={LuAlertTriangle} className="w-10 h-10 text-amber-500 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-amber-800 mb-2">عذراً، التسجيل مغلق حالياً</h3>
              <p className="text-amber-700 text-sm">نكتفي حالياً بالعدد الموجود من المسوقين. يرجى المحاولة في وقت لاحق.</p>
              <button onClick={() => setView('login')} className="mt-4 px-6 py-2 bg-amber-600 text-white rounded-xl font-bold hover:bg-amber-700">العودة للدخول</button>
            </div>
          ) : (
            <>
              <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 mb-6 h-[400px] overflow-y-auto quill-content">
                <div dangerouslySetInnerHTML={{ __html: settings.affiliate_terms || '<p>لا توجد شروط مكتوبة حالياً.</p>' }} />
              </div>
              
              <label className="flex items-center gap-3 cursor-pointer mb-6 p-4 border-2 border-slate-100 rounded-xl hover:bg-slate-50 transition-colors">
                <input type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} className="w-5 h-5 accent-blue-600 cursor-pointer" />
                <span className="font-bold text-slate-800">قرأت الشروط والأحكام وأوافق عليها بالكامل.</span>
              </label>
              
              <div className="flex gap-3">
                <button onClick={() => setView('login')} className="flex-1 py-3 bg-slate-100 text-slate-700 font-bold rounded-xl hover:bg-slate-200 transition-colors">إلغاء</button>
                <button onClick={() => setView('register')} disabled={!termsAccepted} className="flex-1 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50">متابعة للتسجيل</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ---------------- REGISTER VIEW ---------------- */}
      {view === 'register' && (
        <div className="w-full max-w-md mt-10 bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
          <h2 className="text-xl font-black text-slate-900 mb-2">{settings.affiliate_reg_title || 'إنشاء حساب مسوق'}</h2>
          <p className="text-slate-500 text-sm mb-6">{settings.affiliate_reg_subtitle || 'أدخل بياناتك لإنشاء حسابك والبدء في التسويق'}</p>
          <form onSubmit={handleRegister} className="space-y-4">
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1">الاسم الكامل</label>
              <input required type="text" value={regData.name} onChange={e => setRegData({...regData, name: e.target.value})} className="w-full p-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none" placeholder="اكتب اسمك الحقيقي الكامل" />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1">رقم الهاتف (للتواصل والدفع)</label>
              <input required type="text" value={regData.phone} onChange={e => setRegData({...regData, phone: e.target.value})} dir="ltr" className="w-full p-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-right" placeholder="0555000000" />
            </div>
            <div>
              <label className="block text-sm font-bold text-slate-700 mb-1">كلمة المرور</label>
              <input required type="password" value={regData.password} onChange={e => setRegData({...regData, password: e.target.value})} dir="ltr" className="w-full p-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-right" placeholder="••••••••" minLength="6" />
              <p className="text-xs text-slate-400 mt-1">6 أحرف على الأقل</p>
            </div>
            <div className="pt-2 border-t border-slate-100">
              <label className="block text-sm font-bold text-slate-700 mb-1 flex items-center gap-1.5">
                <SafeIcon icon={LuUserPlus} className="w-4 h-4 text-emerald-500" /> كود المسوق الداعي (اختياري)
              </label>
              <p className="text-xs text-slate-500 mb-2">إذا قام مسوق آخر بدعوتك للموقع، أدخل الكود الخاص به هنا.</p>
              <input type="text" value={regData.referred_by} onChange={e => setRegData({...regData, referred_by: e.target.value.toUpperCase()})} dir="ltr" className="w-full p-3 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none text-right bg-emerald-50/30 font-bold" placeholder="مثال: M-1234" />
            </div>
            <div className="pt-4 flex gap-3">
              <button type="button" onClick={() => setView('terms')} className="px-4 py-3 bg-slate-100 text-slate-700 font-bold rounded-xl hover:bg-slate-200">رجوع</button>
              <button type="submit" disabled={loading} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl transition-colors">
                {loading ? 'جاري التسجيل...' : 'إنشاء الحساب'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* ---------------- DASHBOARD VIEW ---------------- */}
      {view === 'dashboard' && marketerData && (
        <div className="w-full">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
            <div>
              <h2 className="text-2xl font-black text-slate-900 mb-2">مرحباً، {marketerData.name}</h2>
              <div className="flex items-center gap-3">
                {renderStatusBadge(marketerData.status)}
              </div>
            </div>
            <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-600 rounded-xl font-bold hover:bg-red-100 transition-colors">
              <SafeIcon icon={LuLogOut} className="w-4 h-4" /> تسجيل خروج
            </button>
          </div>

          {marketerData.status === 'pending' && (
            <div className="bg-amber-50 border-2 border-amber-200 p-6 rounded-2xl mb-8 text-center max-w-2xl mx-auto mt-10">
              <SafeIcon icon={LuClock} className="w-12 h-12 text-amber-500 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-amber-800 mb-2">حسابك قيد المراجعة</h3>
              <p className="text-amber-700">لا يمكنك البدء في التسويق حتى توافق الإدارة على حسابك. يرجى الانتظار، سيتم تفعيل حسابك قريباً.</p>
            </div>
          )}

          {marketerData.status === 'rejected' && (
            <div className="bg-red-50 border-2 border-red-200 p-6 rounded-2xl mb-8 text-center max-w-2xl mx-auto mt-10">
              <SafeIcon icon={LuXCircle} className="w-12 h-12 text-red-500 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-red-800 mb-2">تم رفض حسابك</h3>
              <p className="text-red-700">عذراً، لقد تم رفض طلب انضمامك لبرنامج التسويق أو تم إيقاف حسابك من قبل الإدارة.</p>
            </div>
          )}

          {marketerData.status === 'approved' && (
            <>
              {/* التبويبات الداخلية للوحة المسوق */}
              <div className="flex flex-wrap gap-2 mb-6 bg-white p-2 rounded-2xl border border-slate-200 shadow-sm w-fit">
                <button onClick={() => setActiveTab('dashboard')} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm transition-colors ${activeTab === 'dashboard' ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
                  <SafeIcon icon={LuTrendingUp} className="w-4 h-4" /> لوحة التحكم
                </button>
                <button onClick={() => setActiveTab('network')} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm transition-colors ${activeTab === 'network' ? 'bg-purple-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
                  <SafeIcon icon={LuNetwork} className="w-4 h-4" /> فريقي والشبكة
                </button>
                <button onClick={() => setActiveTab('cars')} className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm transition-colors ${activeTab === 'cars' ? 'bg-pink-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
                  <SafeIcon icon={LuCar} className="w-4 h-4" /> سيارات وعمولات
                </button>
              </div>

              {activeTab === 'dashboard' && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Sidebar: Stats & Code */}
                  <div className="lg:col-span-1 space-y-6">
                    {/* Coupon Code Card */}
                    <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-6 rounded-2xl shadow-md text-white text-center">
                      <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                        <SafeIcon icon={LuTicket} className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="font-bold text-blue-100 mb-1">كود الخصم والدعوة الخاص بك</h3>
                      <p className="text-xs text-blue-200 mb-4">أعطِ هذا الكود للزبائن ليقوموا بإدخاله عند طلب السيارات، أو للمسوقين الجدد عند التسجيل.</p>
                      <div className="bg-white/10 p-4 rounded-xl border border-white/20 backdrop-blur-sm mb-4">
                        <span className="text-3xl font-black tracking-widest">{marketerData.code}</span>
                      </div>
                      <button onClick={copyCode} className="w-full flex items-center justify-center gap-2 py-3 bg-white text-blue-700 rounded-xl font-bold hover:bg-blue-50 transition-colors">
                        <SafeIcon icon={copiedCode ? LuCheck : LuCopy} className="w-5 h-5" />
                        {copiedCode ? 'تم النسخ بنجاح!' : 'نسخ الكود'}
                      </button>
                    </div>

                    {/* Earnings Summary */}
                    <div className="bg-emerald-50 p-5 rounded-2xl border border-emerald-100">
                      <h3 className="font-black text-emerald-800 mb-4 flex items-center gap-2"><SafeIcon icon={LuDollarSign}/> ملخص الأرباح</h3>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center bg-white px-4 py-3 rounded-xl border border-emerald-100">
                          <span className="text-sm font-bold text-slate-600">أرباحي المباشرة:</span>
                          <span className="text-lg font-black text-emerald-600">{myEarnings.toLocaleString()} دج</span>
                        </div>
                        <div className="flex justify-between items-center bg-white px-4 py-3 rounded-xl border border-emerald-100">
                          <span className="text-sm font-bold text-slate-600">أرباحي من فريقي:</span>
                          <span className="text-lg font-black text-emerald-600">{teamEarnings.toLocaleString()} دج</span>
                        </div>
                        <div className="flex justify-between items-center bg-emerald-600 px-4 py-3 rounded-xl text-white mt-2 shadow-sm">
                          <span className="text-sm font-bold">إجمالي الأرباح المشروعة:</span>
                          <span className="text-xl font-black">{totalEarnings.toLocaleString()} دج</span>
                        </div>
                      </div>
                      <p className="text-[10px] text-emerald-600 mt-3 text-center">يتم احتساب الأرباح فقط للطلبات التي تم إغلاقها بنجاح كـ "تمت البيعة"</p>
                    </div>

                    <div className="bg-blue-50 p-5 rounded-2xl border border-blue-100">
                      <h3 className="font-bold text-blue-800 mb-3 flex items-center gap-2"><SafeIcon icon={LuTrendingUp}/> إحصائيات الطلبات</h3>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center bg-white px-4 py-3 rounded-xl border border-blue-100">
                          <span className="text-sm font-bold text-slate-600">طلباتي المباشرة:</span>
                          <span className="text-xl font-black text-blue-600">{leads.length}</span>
                        </div>
                        <div className="flex justify-between items-center bg-white px-4 py-3 rounded-xl border border-blue-100">
                          <span className="text-sm font-bold text-slate-600">طلبات فريقي:</span>
                          <span className="text-xl font-black text-blue-600">{networkLeads.length}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Main: Direct Leads Table */}
                  <div className="lg:col-span-2">
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                      <div className="p-5 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                        <h3 className="font-bold text-slate-800 flex items-center gap-2"><SafeIcon icon={LuUsers} className="w-5 h-5 text-blue-600"/> طلباتي المباشرة</h3>
                        <span className="text-xs font-bold text-slate-500 bg-white px-3 py-1 rounded-lg border border-slate-200">{leads.length} طلب</span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-slate-100">
                          <thead className="bg-white">
                            <tr>
                              <th className="px-5 py-3 text-start text-xs font-black text-slate-500 uppercase">اسم الزبون</th>
                              <th className="px-5 py-3 text-start text-xs font-black text-slate-500 uppercase">السيارة</th>
                              <th className="px-5 py-3 text-start text-xs font-black text-slate-500 uppercase">عمولتي</th>
                              <th className="px-5 py-3 text-start text-xs font-black text-slate-500 uppercase">حالة الطلب</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-50">
                            {leads.length === 0 ? (
                              <tr><td colSpan="4" className="text-center py-10 text-slate-400 text-sm">لا توجد طلبات مسجلة باسمك حتى الآن. شارك الكود الخاص بك مع الزبائن!</td></tr>
                            ) : (
                              leads.map(lead => {
                                const commission = Number(lead.cars_for_sale?.commission) || 0;
                                return (
                                  <tr key={lead.id} className="hover:bg-slate-50 transition-colors">
                                    <td className="px-5 py-4">
                                      <div className="font-bold text-slate-800 text-sm">{lead.customer_name}</div>
                                      <div className="text-xs text-slate-400 mt-1">{format(new Date(lead.created_at), 'yyyy-MM-dd')}</div>
                                    </td>
                                    <td className="px-5 py-4 text-sm text-slate-600">
                                      {lead.cars_for_sale ? `${lead.cars_for_sale.brand} ${lead.cars_for_sale.model}` : 'سيارة محذوفة'}
                                    </td>
                                    <td className="px-5 py-4">
                                      {lead.status === 'closed' ? (
                                        <span className="text-emerald-600 font-black text-sm">+{commission.toLocaleString()} دج</span>
                                      ) : (
                                        <span className="text-slate-400 font-bold text-sm">{commission.toLocaleString()} دج</span>
                                      )}
                                    </td>
                                    <td className="px-5 py-4">
                                      {lead.status === 'new' && <span className="bg-orange-50 text-orange-700 border border-orange-200 px-2.5 py-1 rounded-md text-xs font-bold">قيد الانتظار</span>}
                                      {lead.status === 'contacted' && <span className="bg-blue-50 text-blue-700 border border-blue-200 px-2.5 py-1 rounded-md text-xs font-bold">تم التواصل</span>}
                                      {lead.status === 'closed' && <span className="bg-emerald-50 text-emerald-700 border border-emerald-200 px-2.5 py-1 rounded-md text-xs font-bold">تمت البيعة</span>}
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'network' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-2xl p-6 shadow-md flex items-center justify-between flex-wrap gap-4">
                    <div>
                      <h3 className="text-xl font-black mb-1 flex items-center gap-2"><SafeIcon icon={LuNetwork} /> شبكتي التسويقية</h3>
                      <p className="text-purple-100 text-sm">قم بدعوة مسوقين آخرين باستخدام الكود الخاص بك واحصل على عمولات (عمولة الأب) على كل بيعة يحققونها!</p>
                    </div>
                    <div className="bg-white/10 px-4 py-2 rounded-xl backdrop-blur-sm border border-white/20 text-center">
                      <p className="text-xs text-purple-200 font-bold mb-0.5">عدد مسوقي فريقي</p>
                      <p className="text-2xl font-black">{networkTeam.length}</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Team Members */}
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                      <div className="p-5 border-b border-slate-100 bg-slate-50">
                        <h3 className="font-bold text-slate-800 flex items-center gap-2"><SafeIcon icon={LuUsers} className="w-5 h-5 text-purple-600"/> أعضاء الفريق</h3>
                      </div>
                      <div className="max-h-[400px] overflow-y-auto">
                        <table className="min-w-full divide-y divide-slate-100">
                          <thead className="bg-white sticky top-0">
                            <tr>
                              <th className="px-5 py-3 text-start text-xs font-black text-slate-500 uppercase">اسم المسوق</th>
                              <th className="px-5 py-3 text-start text-xs font-black text-slate-500 uppercase">تاريخ الانضمام</th>
                              <th className="px-5 py-3 text-start text-xs font-black text-slate-500 uppercase">الحالة</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-50">
                            {networkTeam.length === 0 ? (
                              <tr><td colSpan="3" className="text-center py-10 text-slate-400 text-sm">لا يوجد أعضاء في فريقك حتى الآن.</td></tr>
                            ) : (
                              networkTeam.map(member => (
                                <tr key={member.id} className="hover:bg-slate-50 transition-colors">
                                  <td className="px-5 py-4 font-bold text-slate-800 text-sm">{member.name}</td>
                                  <td className="px-5 py-4 text-xs text-slate-500">{format(new Date(member.created_at), 'yyyy-MM-dd')}</td>
                                  <td className="px-5 py-4">{renderStatusBadge(member.status)}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Team Leads */}
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                      <div className="p-5 border-b border-slate-100 bg-slate-50">
                        <h3 className="font-bold text-slate-800 flex items-center gap-2"><SafeIcon icon={LuTrendingUp} className="w-5 h-5 text-emerald-600"/> مبيعات فريقي (أرباحي)</h3>
                      </div>
                      <div className="max-h-[400px] overflow-y-auto">
                        <table className="min-w-full divide-y divide-slate-100">
                          <thead className="bg-white sticky top-0">
                            <tr>
                              <th className="px-5 py-3 text-start text-xs font-black text-slate-500 uppercase">المسوق (كود)</th>
                              <th className="px-5 py-3 text-start text-xs font-black text-slate-500 uppercase">السيارة</th>
                              <th className="px-5 py-3 text-start text-xs font-black text-slate-500 uppercase">ربحي (عمولة الأب)</th>
                              <th className="px-5 py-3 text-start text-xs font-black text-slate-500 uppercase">الحالة</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-50">
                            {networkLeads.length === 0 ? (
                              <tr><td colSpan="4" className="text-center py-10 text-slate-400 text-sm">لم يحقق فريقك أي طلبات حتى الآن.</td></tr>
                            ) : (
                              networkLeads.map(lead => {
                                const parentCommission = Number(lead.cars_for_sale?.parent_commission) || 0;
                                return (
                                  <tr key={lead.id} className="hover:bg-slate-50 transition-colors">
                                    <td className="px-5 py-4 font-bold text-slate-800 text-xs">{lead.marketer_code}</td>
                                    <td className="px-5 py-4 text-xs text-slate-600">
                                      {lead.cars_for_sale ? `${lead.cars_for_sale.brand} ${lead.cars_for_sale.model}` : 'سيارة محذوفة'}
                                    </td>
                                    <td className="px-5 py-4">
                                      {lead.status === 'closed' ? (
                                        <span className="text-emerald-600 font-black text-sm">+{parentCommission.toLocaleString()} دج</span>
                                      ) : (
                                        <span className="text-slate-400 font-bold text-sm">{parentCommission.toLocaleString()} دج</span>
                                      )}
                                    </td>
                                    <td className="px-5 py-4">
                                      {lead.status === 'new' && <span className="text-orange-500 font-bold text-xs">في الانتظار</span>}
                                      {lead.status === 'contacted' && <span className="text-blue-500 font-bold text-xs">تم التواصل</span>}
                                      {lead.status === 'closed' && <span className="text-emerald-600 font-bold text-xs">تمت البيعة</span>}
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'cars' && (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-pink-50 to-rose-50 border border-pink-100 rounded-2xl p-5 flex items-start gap-4">
                    <div className="w-12 h-12 bg-pink-200 rounded-xl flex items-center justify-center flex-shrink-0">
                      <SafeIcon icon={LuHeartHandshake} className="w-6 h-6 text-pink-700" />
                    </div>
                    <div>
                      <h3 className="text-lg font-black text-pink-900 mb-1">السيارات المتاحة للتسويق والعمولات</h3>
                      <p className="text-sm text-pink-700 leading-relaxed">هنا تجد قائمة بجميع السيارات المتوفرة للبيع حالياً في الموقع. يمكنك رؤية <strong>العمولة الصافية</strong> التي ستحصل عليها عند إتمام كل بيعة عن طريق الكود الخاص بك. انسخ رابط السيارة وشاركه مع زبائنك!</p>
                    </div>
                  </div>

                  {marketCars.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
                      <SafeIcon icon={LuCar} className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                      <p className="text-slate-500 font-bold">لا توجد سيارات متاحة للتسويق حالياً.</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                      {marketCars.map(car => {
                        const displayPrice = Number(car.total_price) > 0 ? Number(car.total_price) : (Number(car.price) || 0) + (Number(car.customs_price) || 0) + (Number(car.shipping_price) || 0);
                        const commission = Number(car.commission) || 0;
                        const parentCommission = Number(car.parent_commission) || 0;
                        
                        return (
                          <div key={car.id} className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow flex flex-col">
                            <div className="h-40 bg-slate-100 relative">
                              <img src={car.image_url || 'https://images.unsplash.com/photo-1494905998459-36430ba12391?auto=format&fit=crop&q=80&w=400'} alt="" className="w-full h-full object-cover" />
                              <div className="absolute top-2 right-2 bg-black/60 text-white text-xs font-bold px-2 py-1 rounded-lg backdrop-blur-sm">
                                {car.year}
                              </div>
                            </div>
                            <div className="p-4 flex-1 flex flex-col">
                              <h4 className="font-black text-slate-900 mb-1 line-clamp-1">{car.brand} {car.model}</h4>
                              <p className="text-sm font-bold text-slate-500 mb-4">سعر البيع: <span className="text-slate-800">{displayPrice.toLocaleString()} دج</span></p>
                              
                              <div className="mt-auto space-y-2 mb-4">
                                <div className="bg-pink-50 border border-pink-100 rounded-xl p-2.5 text-center">
                                  <p className="text-[11px] font-bold text-pink-600 mb-0.5">ربحك إذا سوقتها مباشرة</p>
                                  <p className="text-base font-black text-pink-700">{commission.toLocaleString()} دج</p>
                                </div>
                                <div className="bg-purple-50 border border-purple-100 rounded-xl p-2.5 text-center">
                                  <p className="text-[11px] font-bold text-purple-600 mb-0.5">ربحك إذا باعها أحد أفراد فريقك</p>
                                  <p className="text-base font-black text-purple-700">{parentCommission.toLocaleString()} دج</p>
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-2 gap-2">
                                <button onClick={() => copyCarLink(car.id)} className="flex items-center justify-center gap-1.5 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-colors">
                                  <SafeIcon icon={copiedLink === car.id ? LuCheck : LuCopy} className="w-4 h-4" /> {copiedLink === car.id ? 'تم النسخ' : 'نسخ الرابط'}
                                </button>
                                <a href={`/#/car/${car.id}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-xl text-xs font-bold transition-colors">
                                  <SafeIcon icon={LuExternalLink} className="w-4 h-4" /> عرض السيارة
                                </a>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AffiliatePage;